import TelegramLogo from "assets/images/telegram.png";
import { ReactComponent as TwitterLogo } from 'assets/svg/twitter_logo.svg';
import { parseLocalActivity } from 'components/AccountDrawer/MiniPortfolio/Activity/parseLocal'
import { PortfolioLogo } from 'components/AccountDrawer/MiniPortfolio/PortfolioLogo'
import PortfolioRow from 'components/AccountDrawer/MiniPortfolio/PortfolioRow'
import Column, { AutoColumn } from 'components/Column'
import { BASE_URL } from "constants/url";
import useENSName from 'hooks/useENSName'
import { useCombinedActiveList } from 'state/lists/hooks'
import { useTransaction } from 'state/transactions/hooks'
import { TransactionDetails } from 'state/transactions/types'
import styled from 'styled-components/macro'
import { EllipsisStyle, ThemedText } from 'theme'
import { colors } from 'theme/colors';
import { ExplorerDataType, getExplorerLink } from 'utils/getExplorerLink'

import { ButtonPrimary } from "../Button";
import SuccessTxBadge from "../Icons/SuccessTxBadge";
import Row from "../Row";
import { PopupAlertTriangle } from './FailedNetworkSwitchPopup'
import {useAccount} from "wagmi";

const TransactionPopupContentWrapper = styled(Column)`
  padding: 40px 30px;
  align-items: center;
  gap: 16px;
  background-color: ${colors.neutralMid};
  border-radius: 12px;
`

const Descriptor = styled(ThemedText.BodySmall)`
  ${EllipsisStyle}
`

const ExplorerLink = styled.a`
  color: ${colors.primaryBase};
  font-size: 14px;
  line-height: 22px;
  text-align: center;
  text-decoration-line: underline;
`

const SuccessSwapPopupWrapper = styled(AutoColumn)`
  padding: 24px;
`

const StyledTelegramLogo = styled.img`
  width: 24px;
  height: 24px;
`

const SocialButton = styled(ButtonPrimary)`
  height: 36px;
  font-weight: 400;
  font-size: 14px;
  color: ${colors.neutralBase},
`

function TransactionPopupContent({ tx, chainId }: { tx: TransactionDetails; chainId: number }) {
  const success = tx.receipt?.status === 1
  const tokens = useCombinedActiveList()
  const activity = parseLocalActivity(tx, chainId, tokens)
  const { ENSName } = useENSName(activity?.otherAccount)

  if (!activity) return null

  const shareTransaction = (socialMedia: 'twitter' | 'telegram') => {
    const text =
      `I swapped ${activity.descriptor} on CratSwap just now!\n` +
      '\n' +
      ` ${explorerUrl}\n` +
      '\n' +
      ' Come and trade tokens with me and enjoy the amazing trading experience provided by CratSwap\n'
    if (socialMedia === 'twitter') {
      window.open(`https://twitter.com/intent/tweet/?text=${text}&url=${BASE_URL}`)
    } else {
      window.open(`https://telegram.me/share/url?url=${BASE_URL}&text=${text}`)
    }
  }

  const explorerUrl = getExplorerLink(chainId, tx.hash, ExplorerDataType.TRANSACTION)

  return activity.title === 'Swapped' && success ? (
    <SuccessSwapPopupWrapper>
      <ThemedText.SubHeader mb="16px" fontSize={24} lineHeight="31px" color={colors.light}>
        Success
      </ThemedText.SubHeader>
      <TransactionPopupContentWrapper>
        <SuccessTxBadge />
        <ThemedText.SubHeader fontSize={24} lineHeight="31px" textAlign="center">{activity.descriptor}</ThemedText.SubHeader>
        <ExplorerLink href={explorerUrl} target="_blank" rel="noreferrer">View on CratD2Cscan</ExplorerLink>
        <ThemedText.SubHeader fontSize={14} fontWeight={400} color={colors.gray200} textAlign="center">
          Share your swap on Web 2 social media and meet our fellow!
        </ThemedText.SubHeader>
        <Row gap="16px" justifyContent="center">
          <SocialButton onClick={() => shareTransaction('twitter')}>
            <TwitterLogo />
            &nbsp;Twitter
          </SocialButton>
          <SocialButton onClick={() => shareTransaction('telegram')}>
            <StyledTelegramLogo src={TelegramLogo} />
            &nbsp;Telegram
          </SocialButton>
        </Row>
      </TransactionPopupContentWrapper>
    </SuccessSwapPopupWrapper>
  ) : (
    <PortfolioRow
      left={
        success ? (
          <Column>
            <PortfolioLogo
              chainId={chainId}
              currencies={activity.currencies}
              images={activity.logos}
              accountAddress={activity.otherAccount}
            />
          </Column>
        ) : (
          <PopupAlertTriangle />
        )
      }
      title={<ThemedText.SubHeader fontWeight={500}>{activity.title}</ThemedText.SubHeader>}
      descriptor={
        <Descriptor color="textSecondary">
          {activity.descriptor}
          {ENSName ?? activity.otherAccount}
        </Descriptor>
      }
      onClick={() => window.open(explorerUrl, '_blank')}
    />
  )
}

export default function TransactionPopup({ hash }: { hash: string }) {
  const { chainId } = useAccount()

  const tx = useTransaction(hash)

  if (!chainId || !tx) return null

  return <TransactionPopupContent tx={tx} chainId={chainId} />
}
